import React from "react";
import siteConfig from "../config/siteConfig";
import PolicyLayout from "./PolicyLayout";

const privacySections = [
  {
    title: "Who this notice covers",
    body: [
      `This notice explains how ${siteConfig.brandName} handles personal information when you browse this website, contact us, use a customer feature or place an order. It also explains your choices and how to reach us.`,
      "A payment provider, delivery carrier or linked website may separately decide how it handles information for its own service. Read the privacy notice shown by that provider as well as this one.",
    ],
  },
  {
    title: "Information we collect",
    body: [
      "The information depends on how you use the store. We collect only information reasonably connected with operating, protecting and improving the service.",
    ],
    items: [
      "Identity and contact details, such as name and email address.",
      "Order and delivery details, such as products, amount, address, district, delivery instructions and order reference.",
      "Customer-care content, including the subject and message you submit and our response.",
      "Payment and transaction information supplied by the payment provider, such as payment status, method type and provider reference; we do not intentionally store complete card numbers or security codes in this storefront.",
      "Device and usage information, such as browser type, approximate location derived from an IP address, page interactions, security events and error logs where the hosting environment or service providers make these available.",
      "Cart and favourite product identifiers saved in local browser storage on your device.",
    ],
  },
  {
    title: "Where information comes from",
    body: [
      "We receive information directly from you, automatically from your device or browser, and from providers involved in payment, fraud screening, hosting, fulfilment and delivery. We may also receive an updated delivery status or a report about a disputed transaction from those providers.",
    ],
  },
  {
    title: "Required and optional information",
    body: [
      "Checkout fields marked as required are needed to validate or fulfil an order. If they are not provided, checkout or delivery may not be possible. Optional fields, such as apartment details or delivery instructions, may help complete delivery but can be left blank.",
      "Contact-form details are voluntary, but we need enough information to understand and reply to the enquiry. Do not include payment credentials or unnecessary sensitive information in a free-text field.",
    ],
  },
  {
    title: "How we use information",
    body: [
      "We use personal information only for the purposes described at collection, compatible purposes, or another purpose permitted with the required notice or consent.",
    ],
    items: [
      "To validate, accept, prepare, deliver, cancel, return and refund orders.",
      "To process payment and respond to payment disputes.",
      "To answer questions, resolve complaints and provide product, allergen or accessibility support.",
      "To remember a cart or favourites on the device and provide requested account features.",
      "To maintain, troubleshoot, secure and measure the website and prevent fraud or misuse.",
      "To keep tax, accounting, safety, recall and other records required by law.",
      "To send direct marketing only after giving any required notice and obtaining any consent required by applicable law.",
    ],
  },
  {
    title: "When information is shared",
    body: [
      "We share only information reasonably needed for the relevant purpose. Recipients may include hosting and technology providers, payment processors, fraud-prevention services, warehouses, suppliers handling a product issue, delivery carriers, customer-care services, professional advisers and public authorities.",
      "A provider acting for us may use information only for the instructed service and related legal duties. Some recipients, such as banks, card networks, carriers and authorities, may also handle information under their own legal responsibilities. We do not sell personal information for money.",
    ],
  },
  {
    title: "International handling",
    body: [
      "Providers and recipients may process information outside the place where it was collected. Where applicable law requires a transfer safeguard, we will use an appropriate contractual, legal or organisational measure and limit the information transferred.",
    ],
  },
  {
    title: "Cookies and local storage",
    body: [
      "This storefront uses local browser storage to keep the shopping cart and favourites on the device. Hosting, security or payment providers may use strictly necessary technologies for their services. If optional analytics or advertising technologies are introduced, we will update the Cookie policy and provide any consent choice required by law before using them.",
    ],
  },
  {
    title: "Direct marketing",
    body: [
      `We do not treat silence as consent where the law requires an opt-in. You may withdraw a marketing consent without charge by using the unsubscribe method in the message or emailing ${siteConfig.contact.email}. Service messages about an order, security event, recall or policy change are not marketing.`,
    ],
  },
  {
    title: "Retention",
    body: [
      "We retain information only while reasonably needed for the stated purpose and applicable tax, accounting, product-safety, fraud-prevention, dispute and legal requirements. Retention varies by record type. We then delete or anonymise information where practicable, unless law requires or permits continued retention.",
      "Cart and favourite information saved locally remains on the device until it is cleared by the customer, browser or website logic.",
    ],
  },
  {
    title: "Security",
    body: [
      "We use reasonable technical and organisational safeguards appropriate to the information and service. Payment credentials are handled by the provider shown at checkout. No internet transmission or storage system is completely secure, so please report a suspicious message or account event promptly.",
    ],
  },
  {
    title: "Your choices and rights",
    body: [
      `Depending on applicable law, you may request access to or correction of personal information, and may have rights to deletion, restriction, objection, portability or withdrawal of consent. Email ${siteConfig.contact.email} with the subject “Privacy request”. We may verify identity before disclosing or changing information.`,
      "We will respond within the period required by applicable law and explain any lawful exception. You may also complain to the privacy or data-protection authority with jurisdiction over the matter.",
    ],
  },
  {
    title: "Children",
    body: [
      "The store is intended for people able to enter a binding purchase contract. We do not knowingly ask a child to create an account or submit an order without appropriate adult involvement. If you believe a child provided personal information improperly, contact us so we can review it.",
    ],
  },
  {
    title: "Changes and contact",
    body: [
      `We may update this notice when the service, providers or legal requirements change. The date shown above identifies the current version. Questions, privacy requests and concerns can be sent to ${siteConfig.contact.email}.`,
    ],
  },
];

function Privacy() {
  return (
    <PolicyLayout
      eyebrow="Legal"
      title="Privacy policy"
      intro={`Your trust matters. This notice explains how ${siteConfig.brandName} handles personal information when you visit or shop with us.`}
      sections={privacySections}
    />
  );
}

export default Privacy;

